'use client';

import { Box, Stack, type SxProps } from '@mui/material';
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { campaignManagerTheme } from '@campaign-manager/theme';

import { ProseCompact } from '../Prose';
import Text from '../Text';

// Types
interface HintConfig {
  id: string;
  title?: string;
  content: ReactNode;
  priority?: number;
}

interface FormAsideContextValue {
  registerHint: (id: string, config: Omit<HintConfig, 'id'>) => void;
  unregisterHint: (id: string) => void;
  setHintVisible: (id: string, visible: boolean, offsetTop?: number) => void;
  getVisibleHints: () => string[];
  getHints: () => Map<string, HintConfig>;
  getHintOffset: (id: string) => number | undefined;
  subscribe: (callback: () => void) => () => void;
}

// Context
const FormAsideContext = createContext<FormAsideContextValue | null>(null);

export function useFormAside() {
  const context = useContext(FormAsideContext);
  if (!context) {
    throw new Error('useFormAside must be used within a FormAsideProvider');
  }
  return context;
}

// Optional hook that returns null if context is not available
export function useFormAsideOptional() {
  return useContext(FormAsideContext);
}

// Provider
interface FormAsideProviderProps {
  children: ReactNode;
}

export function FormAsideProvider({ children }: FormAsideProviderProps) {
  // Use refs to store state to avoid context value changes
  const hintsRef = useRef<Map<string, HintConfig>>(new Map());
  const visibleHintsRef = useRef<string[]>([]);
  const hintOffsetsRef = useRef<Map<string, number>>(new Map());
  const subscribersRef = useRef<Set<() => void>>(new Set());

  // Trigger re-render for subscribers
  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach((callback) => callback());
  }, []);

  const registerHint = useCallback(
    (id: string, config: Omit<HintConfig, 'id'>) => {
      hintsRef.current.set(id, { id, ...config });
      notifySubscribers();
    },
    [notifySubscribers],
  );

  const unregisterHint = useCallback(
    (id: string) => {
      hintsRef.current.delete(id);
      hintOffsetsRef.current.delete(id);
      visibleHintsRef.current = visibleHintsRef.current.filter((hintId) => hintId !== id);
      notifySubscribers();
    },
    [notifySubscribers],
  );

  const setHintVisible = useCallback(
    (id: string, visible: boolean, offsetTop?: number) => {
      const current = visibleHintsRef.current;
      let changed = false;

      if (visible && !current.includes(id)) {
        visibleHintsRef.current = [...current, id];
        changed = true;
      } else if (!visible && current.includes(id)) {
        visibleHintsRef.current = current.filter((hintId) => hintId !== id);
        changed = true;
      }

      // Update offset if provided
      if (offsetTop !== undefined) {
        const prevOffset = hintOffsetsRef.current.get(id);
        if (prevOffset !== offsetTop) {
          hintOffsetsRef.current.set(id, offsetTop);
          changed = true;
        }
      }

      if (changed) {
        notifySubscribers();
      }
    },
    [notifySubscribers],
  );

  const getVisibleHints = useCallback(() => visibleHintsRef.current, []);
  const getHints = useCallback(() => hintsRef.current, []);
  const getHintOffset = useCallback((id: string) => hintOffsetsRef.current.get(id), []);

  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  // Stable context value - functions never change
  const valueRef = useRef<FormAsideContextValue>({
    registerHint,
    unregisterHint,
    setHintVisible,
    getVisibleHints,
    getHints,
    getHintOffset,
    subscribe,
  });

  return <FormAsideContext.Provider value={valueRef.current}>{children}</FormAsideContext.Provider>;
}

// Context for layout container ref
const FormAsideLayoutContext = createContext<RefObject<HTMLDivElement | null> | null>(null);

// Layout component that creates the two-column structure
interface FormAsideLayoutProps {
  children: ReactNode;
  asideWidth?: number | string;
  gap?: number;
  sx?: SxProps;
}

export function FormAsideLayout({ children, asideWidth = 320, gap = 4, sx }: FormAsideLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <FormAsideLayoutContext.Provider value={containerRef}>
      <Box
        ref={containerRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: `600px ${typeof asideWidth === 'number' ? `${asideWidth}px` : asideWidth}`, // 600px is the width of the only form (line-item strategy) that is using it
          gap,
          alignItems: 'start',
          ...sx,
        }}
      >
        {children}
      </Box>
    </FormAsideLayoutContext.Provider>
  );
}

export function useFormAsideLayoutContainer() {
  return useContext(FormAsideLayoutContext);
}

// The aside panel that displays hints
interface FormAsidePanelProps {
  sx?: SxProps;
}

export function FormAsidePanel({ sx }: FormAsidePanelProps) {
  const context = useFormAsideOptional();
  const [, forceUpdate] = useState(0);

  // Subscribe to changes
  useEffect(() => {
    if (!context) return;
    return context.subscribe(() => forceUpdate((n) => n + 1));
  }, [context]);

  if (!context) {
    return null;
  }

  const visibleHints = context.getVisibleHints();
  const hints = context.getHints();

  // Sort by priority and show the highest priority visible hint
  const sortedVisibleHints = visibleHints
    .map((id) => hints.get(id))
    .filter(Boolean)
    .sort((a, b) => (b?.priority ?? 0) - (a?.priority ?? 0));

  const activeHint = sortedVisibleHints[0];

  if (!activeHint) {
    return <Box sx={{ ...sx }} />;
  }

  // Get offset for positioning
  const offset = context.getHintOffset(activeHint.id) ?? 0;

  return (
    <Box
      sx={{
        position: 'relative',
        ...sx,
      }}
    >
      <Stack
        sx={{
          gap: 1,
          position: 'absolute',
          top: offset,
          left: 0,
          right: 0,
          backgroundColor: 'oklch(0.8801 0.0294 259.78 / 25%)',
          borderLeft: '3px solid',
          borderColor: campaignManagerTheme.color.grey,
          borderRadius: '0 8px 8px 0',
          py: 2,
          pl: 4,
          pr: 1,
          animation: 'slideInFromLeft 0.3s ease-out',

          '@keyframes slideInFromLeft': {
            from: { opacity: 0, transform: 'translateX(-20px)' },
            to: { opacity: 1, transform: 'translateX(0)' },
          },

          // Arrow pointing left towards the form
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -11,
            top: 16,
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderRight: '8px solid',
            borderRightColor: campaignManagerTheme.color.grey,
          },
        }}
      >
        {activeHint.title && (
          <Text
            size="sm"
            weight="medium"
            color="secondary"
          >
            {activeHint.title}
          </Text>
        )}
        <ProseCompact
          size="sm"
          color={campaignManagerTheme.color.darkGrey}
        >
          {activeHint.content}
        </ProseCompact>
      </Stack>
    </Box>
  );
}

// Hint trigger component - wraps form fields and triggers hint visibility
interface FormHintTriggerProps {
  hintId: string;
  title?: string;
  content: ReactNode;
  priority?: number;
  children: ReactNode;
}

export function FormHintTrigger({
  hintId,
  title,
  content,
  priority = 0,
  children,
}: FormHintTriggerProps) {
  const context = useFormAsideOptional();
  const layoutContainerRef = useContext(FormAsideLayoutContext);
  const ref = useRef<HTMLDivElement>(null);

  // Store config in ref to avoid dependency issues
  const configRef = useRef({ title, content, priority });
  configRef.current = { title, content, priority };

  // Register hint on mount, unregister on unmount
  useEffect(() => {
    if (!context) return;

    context.registerHint(hintId, configRef.current);

    return () => {
      context.unregisterHint(hintId);
    };
    // Only run on mount/unmount - hintId is the only real dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintId]);

  // Track visibility with IntersectionObserver - hint stays hidden until element is visible
  useEffect(() => {
    if (!context || !ref.current) return;

    const element = ref.current;

    const calculateOffset = () => {
      if (!layoutContainerRef?.current || !element) return 0;
      const containerRect = layoutContainerRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      return elementRect.top - containerRect.top;
    };

    // Use IntersectionObserver to track visibility - don't show immediately
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const newOffset = calculateOffset();
          context.setHintVisible(hintId, entry.isIntersecting, newOffset);
        }
      },
      {
        threshold: 0.1, // Require 10% visibility before showing
        rootMargin: '0px',
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      context.setHintVisible(hintId, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintId]);

  return <div ref={ref}>{children}</div>;
}

// Export all components
export default {
  Provider: FormAsideProvider,
  Layout: FormAsideLayout,
  Panel: FormAsidePanel,
  Trigger: FormHintTrigger,
};
